/**
 * How long a member page stays open without the operator adding an image (CAPA #44365).
 */
export const SESSION_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * How long after DoctorUI shows a QR code its link may still open a member. Clocks on the
 * workstation and the phone only need to agree to within this.
 */
export const LINK_VALIDITY_MS = 10 * 60 * 1000;

/**
 * A tab's session with one member. It is kept in sessionStorage, which belongs to the tab,
 * so that a reload cannot start it over.
 */
export interface MemberSession {
  /** The `issuedAt` of the link that started the session, if it carried one */
  issuedAt?: number;
  startedAt: number;
  expiresAt: number;
}

/**
 * Whether the session is over. One that starts after now was stored before the phone's
 * clock was set back, so its remaining time cannot be trusted and it is over too.
 */
export function hasExpired(session: MemberSession, now: number) {
  return now >= session.expiresAt || now < session.startedAt;
}

/**
 * The time DoctorUI showed the QR code, from the link's `issuedAt` in epoch milliseconds.
 * `undefined` when the link carries none.
 */
export function parseIssuedAt(value: string | null) {
  if (value === null) {
    return undefined;
  }
  const issuedAt = Number(value);
  // A garbled stamp cannot vouch for the link, so it reads as the oldest possible one
  return Number.isFinite(issuedAt) ? issuedAt : 0;
}

/**
 * What opening a member link does, see openLink.
 *
 * - `continue`: the tab already holds a session for this link and carries on with it.
 * - `start`: the link opens the member. It makes them the active one on the phone, which
 *   closes the tabs of anyone else, and starts the timeout.
 * - `expired`: the link was minted too long ago. It opens nothing and changes nothing.
 */
export type LinkOutcome =
  | { kind: "continue"; session: MemberSession }
  | { kind: "start"; session: MemberSession }
  | { kind: "expired" };

/**
 * What a member link does when its page mounts (AB#121888).
 *
 * DoctorUI stamps its QR links with the time it minted them (`issuedAt`):
 *
 * - The link that started the tab's stored session continues it, expired or not, so a
 *   reload or Back never hands out a fresh ten minutes.
 * - A stamped link minted within LINK_VALIDITY_MS starts a session. One minted earlier is
 *   an old URL back from Safari's history, its suggestions or a restored tab, and expires.
 * - A link without `issuedAt` predates the stamp. It starts a session straight away,
 *   unless the tab already holds one for this member.
 */
export function openLink(
  stored: MemberSession | undefined,
  issuedAt: number | undefined,
  now: number,
  timeoutMs = SESSION_TIMEOUT_MS
): LinkOutcome {
  if (stored && (issuedAt === undefined || stored.issuedAt === issuedAt)) {
    return { kind: "continue", session: stored };
  }

  if (issuedAt !== undefined && now - issuedAt > LINK_VALIDITY_MS) {
    return { kind: "expired" };
  }

  return {
    kind: "start",
    session: { issuedAt, startedAt: now, expiresAt: now + timeoutMs },
  };
}

export function extendSession(
  session: MemberSession,
  now: number,
  timeoutMs = SESSION_TIMEOUT_MS
): MemberSession {
  return { ...session, expiresAt: now + timeoutMs };
}

function storageKey(memberId: string) {
  return `dermaui:session:${memberId}`;
}

export function readSession(
  storage: Storage,
  memberId: string
): MemberSession | undefined {
  try {
    const value = JSON.parse(storage.getItem(storageKey(memberId)) ?? "null");
    if (
      typeof value?.startedAt === "number" &&
      typeof value?.expiresAt === "number" &&
      (value.issuedAt === undefined || typeof value.issuedAt === "number")
    ) {
      return value;
    }
  } catch (error) {
    console.error("Could not read the member session:", error);
  }
  return undefined;
}

export function writeSession(
  storage: Storage,
  memberId: string,
  session: MemberSession
) {
  try {
    storage.setItem(storageKey(memberId), JSON.stringify(session));
  } catch (error) {
    // The session still runs in memory, it only loses surviving a reload
    console.error("Could not store the member session:", error);
  }
}

/**
 * When a scan on this device last opened a link. It is kept in localStorage, which every
 * tab shares, so that a phone has at most one link open at a time. It names no member, so
 * the phone keeps no member's identifier beyond the tabs themselves.
 */
export interface LatestScan {
  openedAt: number;
}

const LATEST_SCAN_KEY = "dermaui:latestScan";

export function readLatestScan(storage: Storage): LatestScan | undefined {
  try {
    const value = JSON.parse(storage.getItem(LATEST_SCAN_KEY) ?? "null");
    if (typeof value?.openedAt === "number") {
      return value;
    }
  } catch (error) {
    console.error("Could not read the latest scan:", error);
  }
  return undefined;
}

/**
 * Records a scan, unless a later one already took over. A stored scan from the future was
 * recorded before the phone's clock was set back, and is replaced.
 */
export function recordScan(storage: Storage, openedAt: number, now: number) {
  const latest = readLatestScan(storage);
  if (latest && latest.openedAt >= openedAt && latest.openedAt <= now) {
    return;
  }

  try {
    storage.setItem(LATEST_SCAN_KEY, JSON.stringify({ openedAt }));
  } catch (error) {
    console.error("Could not record the scan:", error);
  }
}

/**
 * The session, ended now if a scan on this device opened another link after it started
 * (AB#121888).
 *
 * The phone has one link open at a time, whether the next scan is for another member or
 * for another lesion of the same one. Every older tab closes, including tabs iOS had
 * suspended, the moment they come back. A scan from the future is ignored: it was stored
 * before the phone's clock was set back, and would otherwise close every link opened since.
 */
export function endIfSuperseded(
  session: MemberSession,
  latest: LatestScan | undefined,
  now: number
): MemberSession {
  const isSuperseded =
    latest !== undefined &&
    latest.openedAt > session.startedAt &&
    latest.openedAt <= now;

  return isSuperseded
    ? { ...session, expiresAt: Math.min(session.expiresAt, now) }
    : session;
}
